// @ts-nocheck
import React, { memo, useCallback, useMemo } from 'react';
import { alpha, Box, TableCell, useTheme } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PropTypes from 'prop-types';

import {
  TextField,
  DateField,
  IntegerField,
  FloatField,
  FormulaField,
  DATA_TYPES,
} from '../Toolbar/CellDataType/Fields/Fields';
import {
  useTableDispatch,
  useTableState,
} from '../../context/TableSchemaBuilderContext';
import { useTableSelection } from '../../hooks/useTableSelection';
import { useCellManagement } from '../../hooks/useCellManagement';
import { isValueEmpty } from '../../../../utils/Utils';

// Set default and minimum widths
const DEFAULT_WIDTH = 100;
const MIN_WIDTH = 50;

const compareNumeric = (num1, num2, condition) => {
  const parsedNum1 = parseFloat(num1);
  const parsedNum2 = parseFloat(num2);
  if (Number.isNaN(parsedNum1) || Number.isNaN(parsedNum2)) {
    return false;
  }

  const comparisons = {
    '=': parsedNum1 === parsedNum2,
    '>': parsedNum1 > parsedNum2,
    '<': parsedNum1 < parsedNum2,
    '>=': parsedNum1 >= parsedNum2,
    '<=': parsedNum1 <= parsedNum2,
    '!=': parsedNum1 !== parsedNum2,
  };
  return comparisons[condition] ?? false;
};

const compareDate = (date1, date2, condition) => {
  const parsedDate1 = new Date(date1);
  const parsedDate2 = new Date(date2);
  if (
    Number.isNaN(parsedDate1.getTime()) ||
    Number.isNaN(parsedDate2.getTime())
  ) {
    return false;
  }

  const comparisons = {
    '=': parsedDate1.getTime() === parsedDate2.getTime(),
    '>': parsedDate1 > parsedDate2,
    '<': parsedDate1 < parsedDate2,
    '>=': parsedDate1 >= parsedDate2,
    '<=': parsedDate1 <= parsedDate2,
    '!=': parsedDate1.getTime() !== parsedDate2.getTime(),
  };
  return comparisons[condition] ?? false;
};

const compareText = (text1, text2, condition) => {
  const comparisons = {
    '=': text1 === text2,
    '!=': text1 !== text2,
  };
  return comparisons[condition] ?? false;
};

const compareValues = (value1, value2, condition, type) => {
  const compareFunctions = {
    [DATA_TYPES.INTEGER]: compareNumeric,
    [DATA_TYPES.FLOAT]: compareNumeric,
    [DATA_TYPES.DATE]: compareDate,
    [DATA_TYPES.TEXT]: compareText,
  };

  return (compareFunctions[type] ?? compareText)(value1, value2, condition);
};

const getDisplayText = (value) => {
  if (value == null) return '';

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const Cell = memo(
  ({
    cellId,
    columnName,
    rowIndex,
    setCellRef,
    heightFromVirtualized,
    value: externalValue,
    isFromPagination = false,
  }) => {
    const theme = useTheme();
    const { schema, ui, config } = useTableState();
    const { handleMouseDown, handleMouseOver, handleMouseUp } =
      useTableSelection();
    const { handleCellChange } = useCellManagement();
    const dispatch = useTableDispatch();

    const cellData = schema?.cells?.[cellId] ?? {
      value: '',
      valueType: 'default',
      formula: '',
      dataType: DATA_TYPES.TEXT, // Default data type
    };

    // Use external value if provided and from pagination, otherwise use cell data
    const displayValue =
      isFromPagination && externalValue !== undefined
        ? externalValue
        : cellData.value;

    const displayText = getDisplayText(displayValue);
    const isCurrentCell = cellId === ui?.currentCell;
    const isSelected = ui?.selectedCells?.includes(cellId);
    const format = cellData.format || {};
    const isLocked = cellData.isLocked || false;
    const isFormula = cellData.valueType === 'formula';
    const readOnly = config?.readOnly; // Remove isFromPagination read-only restriction
    const editMode = config?.editMode;

    const handleValueChange = useCallback(
      (value) => {
        handleCellChange(cellId, value);
      },
      [handleCellChange, cellId]
    );

    const handleCellRef = useCallback(
      (el) => {
        setCellRef(el, cellId);
      },
      [setCellRef, cellId]
    );

    const handleFocus = useCallback(() => {
      dispatch({ type: 'SET_EDIT_MODE', payload: true });
    }, [dispatch]);

    const handleBlur = useCallback(() => {
      dispatch({ type: 'SET_EDIT_MODE', payload: false });
    }, [dispatch]);

    const getConditionalStyles = useCallback(() => {
      const value = displayValue;
      const rules = cellData?.conditionalRules || [];
      const dataType = cellData?.dataType || DATA_TYPES.TEXT;

      // Find the first matching rule using Array.find
      const matchingRule = rules.find((rule) =>
        compareValues(value, rule.value, rule.condition, dataType)
      );

      return matchingRule?.styles || {};
    }, [cellData, displayValue]);

    // Add the conditional styles to the TableCell sx prop
    const conditionalStyles = useMemo(
      () => getConditionalStyles(),
      [getConditionalStyles]
    );

    /**
     * Get the column width from schema settings, respecting readOnly mode
     * @returns {number} Width in pixels
     */
    const getColumnWidth = useCallback(() => {
      // Always use the column width from schema settings if available
      if (schema?.columnSettings?.[columnName]?.width) {
        return schema.columnSettings[columnName].width;
      }
      return DEFAULT_WIDTH;
    }, [schema?.columnSettings, columnName]);

    const renderField = useCallback(() => {
      const commonProps = {
        value: displayValue,
        onChange: handleValueChange,
        inputRef: handleCellRef,
        format: isValueEmpty(conditionalStyles) ? format : conditionalStyles,
        onFocus: handleFocus,
        onBlur: handleBlur,
        disabled: (editMode && isLocked) || readOnly,
        sx: {
          backgroundColor:
            format?.backgroundColor || theme?.palette?.other?.white,
          '& .MuiInputBase-input': {
            color: format?.color
              ? `${format.color} !important`
              : theme?.palette?.other?.black,
          },
        },
      };

      if (isFormula) {
        return (
          <FormulaField
            {...commonProps}
            disabled={editMode || readOnly}
            cellId={cellId}
          />
        );
      }

      switch (cellData.dataType) {
        case DATA_TYPES.DATE:
          return <DateField {...commonProps} />;
        case DATA_TYPES.INTEGER:
          return <IntegerField {...commonProps} />;
        case DATA_TYPES.FLOAT:
          return <FloatField {...commonProps} />;
        case DATA_TYPES.TEXT:
        default:
          return <TextField {...commonProps} />;
      }
    }, [
      displayValue,
      cellData,
      handleValueChange,
      handleCellRef,
      conditionalStyles,
      format,
      handleFocus,
      handleBlur,
      readOnly,
      isValueEmpty,
      isLocked,
      editMode,
      isFormula,
      cellId,
    ]);

    const handleCellMouseDown = useCallback(() => {
      // Prevent default behavior if the cell is read-only
      if (readOnly) {
        return;
      }
      handleMouseDown(cellId);
    }, [readOnly, handleMouseDown, cellId]);

    const handleCellMouseOver = useCallback(() => {
      // Prevent default behavior if the cell is read-only
      if (readOnly) {
        return;
      }
      handleMouseOver(cellId);
    }, [readOnly, handleMouseOver, cellId]);

    const handleCellMouseUp = useCallback(() => {
      // Prevent default behavior if the cell is read-only
      if (readOnly) {
        return;
      }
      handleMouseUp();
    }, [readOnly, handleMouseUp]);

    const renderLockIcon = useCallback(() => {
      if (!isLocked) {
        return null;
      }

      return (
        <Box
          sx={{
            position: 'absolute',
            top: '46%',
            right: 0,
            pointerEvents: 'none',
            opacity: 0.5,
          }}
        >
          <LockIcon
            fontSize="small"
            color="action"
            sx={{
              width: '14px',
              height: '14px',
              color: theme?.palette?.other?.toolbarText,
            }}
          />
        </Box>
      );
    }, [isLocked]);

    return (
      <TableCell
        sx={{
          width: getColumnWidth(),
          height:
            schema?.rowSettings?.[rowIndex]?.height || heightFromVirtualized,
          minWidth: MIN_WIDTH,
          padding: '0 !important',
          cursor: readOnly ? 'default' : 'cell',
          position: 'relative',
          border: `1px solid ${alpha(theme?.palette?.primary?.main, 0.1)}`,
          backgroundColor: theme?.palette?.other?.cell,
          // Enhanced selection styling
          ...(isCurrentCell && {
            outline: `2px solid ${theme?.palette?.primary?.main}`,
            zIndex: 2,
          }),
          ...(isSelected &&
            !isCurrentCell && {
              outline: `1px solid ${theme?.palette?.primary?.main}`,
              zIndex: 1,
            }),
          // Focus state
          '&:focus-within': {
            outline: `2px solid ${theme?.palette?.primary?.main}`,
            zIndex: 3,
          },
          '& .MuiFormControl-root.MuiTextField-root': {
            marginTop: 0,
            '& .MuiOutlinedInput-root': {
              borderRadius: '0px',
            },
          },
          '& .MuiInputBase-root.MuiOutlinedInput-root': {
            border: 'none',
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          },
          '& .MuiInputBase-input': {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          },
        }}
        onMouseDown={handleCellMouseDown}
        onMouseOver={handleCellMouseOver}
        onMouseUp={handleCellMouseUp}
      >
        <Box
          sx={{ width: '100%', height: '100%' }}
          title={readOnly && displayText?.length >= 15 ? displayText : ''}
        >
          {renderField()}
        </Box>
        {renderLockIcon()}
      </TableCell>
    );
  }
);

Cell.propTypes = {
  cellId: PropTypes.string.isRequired,
  columnName: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  setCellRef: PropTypes.func.isRequired,
  heightFromVirtualized: PropTypes.number,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFromPagination: PropTypes.bool,
};

export default Cell;
